import React from "react";
import { Link } from "react-router-dom";
import { Skeleton, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

export const BRAND = "#843911";

// تنسيق الأرقام بفواصل عربية-لاتينية موحّدة
export const formatNumber = (value, { decimals = 0 } = {}) => {
  const num = Number(value);
  if (value === null || value === undefined || Number.isNaN(num)) return "-";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const formatMoney = (value) =>
  `${formatNumber(value, { decimals: 0 })} ر.س`;

/**
 * بطاقة "تحتاج إجراءً": رقم كبير + رابط مباشر للقائمة مفلترة.
 * tone يحدد لون الشريط الجانبي حسب درجة الإلحاح.
 */
export const ActionCard = ({
  label,
  count,
  to,
  tone = "neutral",
  hint,
  approximate = false,
  loading = false,
}) => {
  const tones = {
    danger: "#c0392b",
    warning: "#d68910",
    info: "#2471a3",
    neutral: "#7f8c8d",
  };
  const color = tones[tone] || tones.neutral;
  const isEmpty = !loading && !count;

  return (
    <Link
      to={to}
      className="text-decoration-none color-inherit dashboard-action-card"
      style={{ borderInlineStart: `4px solid ${isEmpty ? "#d5dbdb" : color}` }}
    >
      <div className="dashboard-action-card__label">
        {label}
        {hint && (
          <Tooltip title={hint}>
            <InfoCircleOutlined className="ms-1" style={{ fontSize: 12 }} />
          </Tooltip>
        )}
      </div>
      {loading ? (
        <Skeleton.Button active size="small" style={{ width: 60, height: 30 }} />
      ) : (
        <div
          className="dashboard-action-card__value"
          style={{ color: isEmpty ? "#95a5a6" : color }}
        >
          {approximate && count > 0 ? "~" : ""}
          {formatNumber(count)}
        </div>
      )}
      <div className="dashboard-action-card__cta">
        {isEmpty ? "لا شيء معلّق ✓" : "عرض ←"}
      </div>
    </Link>
  );
};

/** بطاقة مؤشر رقمي عام (KPI) */
export const StatCard = ({ label, value, sub, loading = false }) => (
  <div className="dashboard-stat-card">
    <div className="dashboard-stat-card__label">{label}</div>
    {loading ? (
      <Skeleton.Button active size="small" style={{ width: 90, height: 28 }} />
    ) : (
      <div className="dashboard-stat-card__value">{value}</div>
    )}
    {sub && <div className="dashboard-stat-card__sub">{sub}</div>}
  </div>
);

/**
 * قائمة أعمدة أفقية — بديل خفيف عن مكتبة رسوم كاملة.
 * items: [{ label, value }]
 */
export const BarList = ({ items = [], formatter = formatNumber, loading }) => {
  if (loading) return <Skeleton active paragraph={{ rows: 4 }} title={false} />;

  const valid = items.filter((item) => Number(item.value) > 0);
  if (!valid.length) {
    return <div className="text-muted small py-3">لا توجد بيانات</div>;
  }

  const max = Math.max(...valid.map((item) => Number(item.value)));

  return (
    <div className="dashboard-barlist">
      {valid.map(({ label, value, color }) => (
        <div key={label} className="dashboard-barlist__row">
          <div className="dashboard-barlist__label">{label}</div>
          <div className="dashboard-barlist__track">
            <div
              className="dashboard-barlist__fill"
              style={{
                width: `${Math.max((Number(value) / max) * 100, 2)}%`,
                background: color || BRAND,
              }}
            />
          </div>
          <div className="dashboard-barlist__value">{formatter(value)}</div>
        </div>
      ))}
    </div>
  );
};

/** حلقة نسبة مئوية بـ SVG خالص — بدون أي مكتبة خارجية */
export const Donut = ({ value = 0, label, sublabel, color = BRAND }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const radius = 46;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="dashboard-donut">
      <svg viewBox="0 0 120 120" width="120" height="120">
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="#ecf0f1"
          strokeWidth="12"
        />
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
          transform="rotate(-90 60 60)"
        />
        <text
          x="60"
          y="58"
          textAnchor="middle"
          fontSize="22"
          fontWeight="700"
          fill="#2c3e50"
        >
          {formatNumber(pct, { decimals: 1 })}%
        </text>
        <text x="60" y="76" textAnchor="middle" fontSize="11" fill="#7f8c8d">
          {sublabel}
        </text>
      </svg>
      <div className="dashboard-donut__label">{label}</div>
    </div>
  );
};

/** غلاف موحّد لأقسام اللوحة */
export const Panel = ({ title, extra, children }) => (
  <div className="dashboard-panel">
    <div className="dashboard-panel__head">
      <h6 className="dashboard-panel__title">{title}</h6>
      {extra}
    </div>
    {children}
  </div>
);
